<template>
  <div style="background-color:black; height:100%">
    <v-row justify="center">
      <v-col cols="12" md="12">
        <v-card class="edit-po-card">
          <!-- Back Button -->
          <v-btn icon class="back-button" @click="confirmBack">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <!-- Card Title -->
          <v-card-title class="headline my-5">Edit Purchase Order</v-card-title>

          <!-- Card Content -->
          <v-card-text>
            <!-- Purchase Order Form -->
            <v-form ref="form" v-model="valid">
              <!-- Loop through each Purchase Order in poList -->
              <div v-for="(po, poIndex) in poList" :key="poIndex" class="purchase-order-group">
                <!-- Row 1: PO Number, Nexus PO Number, Supplier, Supplier Reference -->
                <v-row>
                  <!-- PO Number Field -->
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="po.purchaseNumber"
                      label="PO Number"
                      dense
                      outlined
                      class="montserrat-input"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <!-- Nexus PO Number Field -->
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="po.nexusPONumber"
                      label="Nexus PO Number"
                      dense
                      outlined
                      class="montserrat-input"
                      :rules="[rules.required, rules.nexusPONumber]"
                    ></v-text-field>
                  </v-col>
                  <!-- Supplier Field -->
                  <v-col cols="12" sm="3">
                    <v-autocomplete
                      v-model="po.supplier"
                      :items="suppliers"
                      item-text="supplierName"
                      item-value="supplierName"
                      label="Supplier"
                      dense
                      outlined
                      class="montserrat-input"
                      :rules="[rules.required]"
                      clearable
                      solo
                    ></v-autocomplete>
                  </v-col>
                  <!-- Supplier Reference Field -->
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="po.supplierReference"
                      label="Supplier Reference"
                      dense
                      outlined
                      class="montserrat-input"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Row 2: Delivery Time, Priority, SO Number, Quote Timestamp -->
                <v-row>
                  <!-- Delivery Time Field -->
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="po.deliveryTime"
                      label="Delivery Time"
                      dense
                      outlined
                      class="montserrat-input"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                  <!-- Priority Field -->
                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="po.priority"
                      :items="priorityOptions"
                      label="Priority"
                      dense
                      outlined
                      class="montserrat-input"
                      :rules="[rules.required]"
                    ></v-select>
                  </v-col>
                  <!-- SO Number Field -->
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="po.soNumber"
                      label="SO Number"
                      dense
                      outlined
                      class="montserrat-input"
                    ></v-text-field>
                  </v-col>
                  <!-- Quote Timestamp Field -->
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="po.quoteTimestamp"
                      label="Quote Timestamp"
                      dense
                      outlined
                      class="montserrat-input"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Row 3: Engineer, Salesperson, Status -->
                <v-row>
                  <!-- Engineer Field -->
                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="po.selectedEngineer"
                      :items="engineers"
                      item-text="fullName"
                      item-value="id"
                      label="Engineer"
                      dense
                      outlined
                      class="montserrat-input"
                      :rules="[rules.required]"
                    ></v-select>
                  </v-col>
                  <!-- Salesperson Field -->
                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="po.selectedSalesperson"
                      :items="salespersons"
                      item-text="fullName"
                      item-value="id"
                      label="Salesperson"
                      dense
                      outlined
                      class="montserrat-input"
                    ></v-select>
                  </v-col>
                  <!-- Status Field -->
                  <v-col cols="12" sm="3">
                    <v-select
                      v-model="po.status"
                      :items="status"
                      label="Status"
                      dense
                      outlined
                      class="montserrat-input"
                      :rules="[rules.required]"
                    ></v-select>
                  </v-col>
                  <!-- Empty Column for layout balance -->
                  <v-col cols="12" sm="3"></v-col>
                </v-row>

                <!-- Items Section -->
                <!-- This section is hidden if current user's purchase orders permission "View_Line_Items_(Labour_and_Stock)" is true -->
                <div v-if="!hidePOItems">
                  <h3 class="section-title my-5">Items</h3>
                  <div v-for="(item, index) in po.items" :key="index" class="item-group">
                    <v-row>
                      <!-- Quantity Field -->
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model="item.quantity"
                          label="Quantity"
                          dense
                          outlined
                          class="montserrat-input"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <!-- Code Field -->
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model="item.code"
                          label="Code"
                          dense
                          outlined
                          class="montserrat-input"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <!-- Description Field -->
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model="item.description"
                          label="Description"
                          dense
                          outlined
                          class="montserrat-input"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <!-- Drawing Number Field -->
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model="item.drawingNumber"
                          label="Drawing Number"
                          dense
                          outlined
                          class="montserrat-input"
                        ></v-text-field>
                      </v-col>
                      <!-- Lead Time Field -->
                      <v-col cols="12" md="2">
                        <v-select
                          v-model="item.leadtime"
                          :items="leadTimeOptions"
                          label="Lead Time"
                          outlined
                          dense
                          class="montserrat-input"
                          :rules="[rules.required]"
                        ></v-select>
                      </v-col>
                      <!-- Remove Item Button -->
                      <v-col cols="12" md="2">
                        <v-btn dark color="red" dense @click="confirmRemoveItem(poIndex, index)" class="montserrat-button my-2">
                          <v-icon class="mx-4" left>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Add Item Button (kept separate) -->
                  <v-row justify="start">
                    <v-btn dark color="blue" dense @click="confirmAddItem(poIndex)" class="montserrat-button">
                      <v-icon left>mdi-plus</v-icon>Add Item
                    </v-btn>
                  </v-row>
                </div>

                <!-- Divider -->
                <v-divider class="my-4"></v-divider>
              </div>

              <!-- Submit Button -->
              <v-row justify="center">
                <v-btn dark dense large width="100%" @click="confirmSubmit" class="montserrat-button">
                  <v-icon left>mdi-check-circle</v-icon>Update Purchase Orders
                </v-btn>
              </v-row>
            </v-form>

            <!-- Loading Overlay -->
            <v-overlay :value="loading">
              <v-progress-circular :size="100" color="white" indeterminate></v-progress-circular>
            </v-overlay>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Submit Confirmation Dialog -->
    <v-dialog v-model="dialogSubmit" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirm Update</v-card-title>
        <v-card-text>Are you sure you want to update these purchase orders?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black" dark dense width="150px" @click="submitConfirmed">Update</v-btn>
          <v-btn color="red" dark dense width="150px" @click="dialogSubmit = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Item Confirmation Dialog -->
    <v-dialog v-model="dialogAddItem" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Add Item</v-card-title>
        <v-card-text>Are you sure you want to add this item?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black" dark dense width="150px" @click="addItemConfirmed">Add</v-btn>
          <v-btn color="red" dark dense width="150px" @click="dialogAddItem = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Item Confirmation Dialog -->
    <v-dialog v-model="dialogRemoveItem" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Remove Item</v-card-title>
        <v-card-text>Are you sure you want to remove this item?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black" dark dense width="150px" @click="removeItemConfirmed">Remove</v-btn>
          <v-btn color="red" dark dense width="150px" @click="dialogRemoveItem = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Back Confirmation Dialog -->
    <v-dialog v-model="dialogBack" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>Are you sure you want to go back without saving?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black" dark dense width="150px" @click="navigateBack">Yes</v-btn>
          <v-btn color="red" dark dense width="150px" @click="dialogBack = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

export default {
  data() {
    return {
      valid: false,
      leadTimeOptions: [
        "1-2 DAYS",
        "2-3 DAYS",
        "3-5 DAYS",
        "4-6 DAYS",
        "5-7 DAYS",
        "6-8 DAYS",
        "8-10 DAYS",
        "10-12 DAYS",
        "10-15 DAYS",
        "15-20 DAYS",
        "6-8 WEEKS",
        "8-10 WEEKS",
        "10-12 WEEKS",
        "Ex - Stock",
      ],
      poList: [],
      suppliers: [],
      engineers: [],
      salespersons: [],
      priorityOptions: ["Not Urgent", "Medium", "Urgent"],
      status: ["In Progress", "Delayed", "Complete"],
      loading: false,
      dialogSubmit: false,
      dialogAddItem: false,
      dialogRemoveItem: false,
      dialogBack: false,
      currentItemIndex: null,
      currentPOIndex: null,
      // Define validation rules
      rules: {
        required: (value) => !!value || "Required.",
        nexusPONumber: (value) => {
          const pattern = /^[A-Z0-9-]+$/;
          return pattern.test(value) || "Invalid Nexus PO Number format.";
        },
        numeric: (value) => !isNaN(value) || "Must be numeric",
        nonNegative: (value) => value >= 0 || "Cannot be negative",
      },
      // For dialogs that confirm actions
      currentUser: null,
    };
  },
  async mounted() {
    this.loading = true;
    await this.fetchCurrentUser();
    await this.fetchInitialData();
    this.loading = false;
  },
  computed: {
    // Computed property to hide the Items section if the current user's purchase orders map
    // has "View_Line_Items_(Labour_and_Stock)" set to true.
    hidePOItems() {
      return (
        this.currentUser &&
        this.currentUser.purchaseOrders &&
        this.currentUser.purchaseOrders["View_Line_Items_(Labour_and_Stock)"] === true
      );
    },
  },
  methods: {
    async fetchCurrentUser() {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;
      if (user) {
        try {
          const db = getFirestore(firebaseApp);
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            this.currentUser = userDoc.data();
          }
        } catch (error) {
          console.error("Error fetching current user:", error);
        }
      }
    },
    async fetchInitialData() {
      await Promise.all([
        this.fetchSuppliers(),
        this.fetchEngineers(),
        this.fetchSalespersons(),
        this.fetchPOData(),
      ]);
    },
    async fetchPOData() {
      const db = getFirestore(firebaseApp);
      const poId = localStorage.getItem("selectedPO");
      if (poId) {
        try {
          const poRef = doc(db, "purchaseOrders", poId);
          const poSnap = await getDoc(poRef);
          if (poSnap.exists()) {
            const poData = poSnap.data();
            this.poList = [
              {
                id: poId,
                purchaseNumber: poData.purchaseNumber || "",
                nexusPONumber: poData.nexusPONumber || "",
                supplier: poData.supplier || "",
                supplierReference: poData.supplierReference || "",
                deliveryTime: poData.deliveryTime || "",
                priority: poData.priority || null,
                soNumber: poData.soNumber || "",
                selectedEngineer: poData.engineer || null,
                selectedSalesperson: poData.salesperson || null,
                quoteTimestamp: poData.quoteTimestamp || "",
                status: poData.status || null,
                items: poData.items || [],
              },
            ];
          } else {
            console.warn("No such Purchase Order found with ID:", poId);
          }
        } catch (error) {
          console.error("Error fetching Purchase Order data:", error);
        }
      } else {
        console.warn("No Purchase Order ID found in localStorage");
      }
    },
    async fetchSuppliers() {
      try {
        const db = getFirestore(firebaseApp);
        const suppliersCollection = collection(db, "suppliers");
        const suppliersSnapshot = await getDocs(suppliersCollection);
        this.suppliers = suppliersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          supplierName: doc.data()["Supplier Name"] || "",
        }));
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    },
    async fetchEngineers() {
      try {
        const db = getFirestore(firebaseApp);
        const usersCollection = collection(db, "users");
        const querySnapshot = await getDocs(usersCollection);
        this.engineers = querySnapshot.docs
          .filter((doc) => doc.data().role === "Engineer")
          .map((doc) => ({
            fullName: doc.data().fullName || "",
            id: doc.id,
          }));
      } catch (error) {
        console.error("Error fetching engineers:", error);
      }
    },
    async fetchSalespersons() {
      try {
        const db = getFirestore(firebaseApp);
        const usersCollection = collection(db, "users");
        const querySnapshot = await getDocs(usersCollection);
        this.salespersons = querySnapshot.docs
          .filter((doc) => doc.data().role === "Salesperson")
          .map((doc) => ({
            fullName: doc.data().fullName || "",
            id: doc.id,
          }));
      } catch (error) {
        console.error("Error fetching salespersons:", error);
      }
    },
    confirmAddItem(poIndex) {
      this.currentPOIndex = poIndex;
      this.dialogAddItem = true;
    },
    addItemConfirmed() {
      if (this.currentPOIndex !== null && this.currentPOIndex >= 0) {
        this.poList[this.currentPOIndex].items.push({
          quantity: "",
          code: "",
          description: "",
          drawingNumber: "",
          leadtime: "",
        });
        this.dialogAddItem = false;
      } else {
        console.error("Invalid Purchase Order Index for adding item");
      }
    },
    confirmRemoveItem(poIndex, itemIndex) {
      this.currentPOIndex = poIndex;
      this.currentItemIndex = itemIndex;
      this.dialogRemoveItem = true;
    },
    removeItemConfirmed() {
      if (
        this.currentPOIndex !== null &&
        this.currentPOIndex >= 0 &&
        this.currentItemIndex !== null &&
        this.currentItemIndex >= 0
      ) {
        const items = this.poList[this.currentPOIndex].items;
        if (items.length > 1) {
          items.splice(this.currentItemIndex, 1);
        } else {
          alert("Cannot remove the last item.");
        }
        this.dialogRemoveItem = false;
      } else {
        console.error("Invalid indices for removing item");
      }
    },
    confirmSubmit() {
      this.dialogSubmit = true;
    },
    async submitConfirmed() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const db = getFirestore(firebaseApp);
          const updatePromises = this.poList.map((po) => {
            const poRef = doc(db, "purchaseOrders", po.id);
            const updatedData = {
              supplier: po.supplier,
              supplierReference: po.supplierReference,
              deliveryTime: po.deliveryTime,
              priority: po.priority,
              soNumber: po.soNumber,
              engineer: po.selectedEngineer,
              salesperson: po.selectedSalesperson,
              quoteTimestamp: po.quoteTimestamp,
              status: po.status,
              items: po.items,
              nexusPONumber: po.nexusPONumber,
            };
            return updateDoc(poRef, updatedData);
          });
          await Promise.all(updatePromises);
          console.log("Purchase orders updated successfully");
          this.dialogSubmit = false;
          this.$router.push("/AllPO");
        } catch (error) {
          console.error("Error updating purchase orders:", error);
        } finally {
          this.loading = false;
        }
      } else {
        console.log("Form validation failed");
      }
    },
    confirmBack() {
      this.dialogBack = true;
    },
    navigateBack() {
      this.dialogBack = false;
      this.$router.go(-1);
    },
    async openPhotoCarousel(item) {
      if (item.pictures && item.pictures.length > 0) {
        const promises = item.pictures.map(async (picture) => {
          try {
            const storageRef = ref(storage, picture);
            const downloadURL = await getDownloadURL(storageRef);
            return downloadURL;
          } catch (error) {
            console.error(`Error fetching download URL for ${picture}:`, error);
            return null;
          }
        });
        try {
          const urls = await Promise.all(promises);
          this.photoDialog.photos = urls.filter(url => url);
          this.photoDialog.visible = true;
        } catch (error) {
          console.error('Error fetching photo URLs:', error);
        }
      } else {
        this.photoDialog.photos = [];
        this.photoDialog.visible = true;
      }
    },
    async downloadPhotos(item) {
      if (!item.pictures || item.pictures.length === 0) {
        alert('No photos available for download.');
        return;
      }
      const JSZip = (await import("jszip")).default;
      const { saveAs } = await import("file-saver");
      const zip = new JSZip();
      const promises = item.pictures.map(async (picture) => {
        try {
          const storageRef = ref(storage, picture);
          const url = await getDownloadURL(storageRef);
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = picture.split('/').pop();
          zip.file(fileName, blob);
        } catch (error) {
          console.error('Error downloading photo:', error);
        }
      });
      await Promise.all(promises);
      try {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'photos.zip');
      } catch (error) {
        console.error('Error generating zip file:', error);
      }
    },
    async downloadAllPhotos() {
      const JSZip = (await import("jszip")).default;
      const { saveAs } = await import("file-saver");
      const allPictures = [];
      this.poList.forEach((po) => {
        if (po.items && po.items.length > 0) {
          po.items.forEach((item, index) => {
            if (item.pictures && item.pictures.length > 0) {
              item.pictures.forEach((pic) => {
                allPictures.push({ path: pic, itemIndex: index });
              });
            }
          });
        }
      });
      if (allPictures.length === 0) {
        alert('No photos available to download.');
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder('All_PO_Photos') || zip;
      const promises = allPictures.map(async (pictureObj) => {
        const { path, itemIndex } = pictureObj;
        try {
          const storageRef = ref(storage, path);
          const url = await getDownloadURL(storageRef);
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = `Item_${itemIndex + 1}_${path.split('/').pop()}`;
          folder.file(fileName, blob);
        } catch (error) {
          console.error(`Error downloading photo ${path}:`, error);
        }
      });
      try {
        await Promise.all(promises);
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'all_photos.zip');
      } catch (error) {
        console.error('Error generating zip file:', error);
        alert('Failed to download all photos. Please try again.');
      }
    },
    triggerFileInput(index) {
      if (this.$refs[`fileInput${index}`] && this.$refs[`fileInput${index}`][0]) {
        this.$refs[`fileInput${index}`][0].click();
      }
    },
    async handleFileUpload(event, index) {
      const files = event.target.files;
      if (files && files.length > 0) {
        for (let file of files) {
          const timestamp = Date.now();
          const storagePath = `purchaseOrders/${this.poList[this.currentPOIndex].id}/${timestamp}_${file.name}`;
          const storageRef = ref(storage, storagePath);
          try {
            await uploadBytes(storageRef, file);
            if (!this.poList[this.currentPOIndex].items[index].pictures) {
              this.$set(this.poList[this.currentPOIndex].items[index], 'pictures', []);
            }
            this.poList[this.currentPOIndex].items[index].pictures.push(storagePath);
          } catch (error) {
            console.error('Error uploading photo:', error);
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.edit-po-card {
  position: relative;
  z-index: 2;
  border-radius: 20px;
  overflow: hidden;
  margin-top: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  padding: 20px;
  transition: all 0.3s ease-in-out;
}

.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}

.montserrat-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.purchase-order-group {
  margin-bottom: 20px;
}

.item-group {
  margin-bottom: 20px;
}

.section-title {
  color: white;
  font-weight: bold;
}

.v-btn {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.v-card-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.back-button {
  position: absolute;
  top: 16px;
  left: 16px;
  background-color: red;
  color: white;
  border-radius: 50%;
}

.back-button:hover {
  transform: scale(1.1);
}
</style>
